import {
  ApolloError,
  LazyQueryHookOptions,
  LazyQueryResult,
  QueryLazyOptions,
  TypedDocumentNode,
  useLazyQuery,
} from '@apollo/client'
import { DocumentNode } from 'graphql'
import { useCallback, useContext } from 'react'

import { useRefetchOnChange } from '../../../hooks/useRefetchOnChange'
import { TokenContext } from '../../../layouts/context'

export type SearchLazyQueryTuple<TData, TVariables> = [
  (options?: QueryLazyOptions<TVariables>) => void,
  Omit<LazyQueryResult<TData, TVariables>, 'error'> & {
    error?: ApolloError | Error
  },
]

export const useSearchLazyQuery = <TData = unknown, TVariables = Record<string, unknown>>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  hookOptions?: LazyQueryHookOptions<TData, TVariables>,
): SearchLazyQueryTuple<TData, TVariables> => {
  const token = useContext(TokenContext)
  const [runLazyQuery, queryResultProps] = useLazyQuery(query, {
    ...hookOptions,
    context: {
      ...(hookOptions && hookOptions.context),
      uri: process.env.GATSBY_SEARCH_URL,
      // set context in hook options so refetched queries will include token
      ...(token && { token }),
    },
  })

  useRefetchOnChange(queryResultProps, [token])

  const runSearchLazyQuery = useCallback(
    /*
     * Disallow setting context in execute function to support use of refetch().
     *
     * @apollo/client 3.6.x no longer uses a "deep" merge to combine hook and execute
     * options. Consequently, any `context` prop specified in execute options overrides
     * the entire `context` prop in query options.
     *
     * Ref: https://github.com/apollographql/apollo-client/blob/v3.6.8/src/react/hooks/useLazyQuery.ts#L37
     *
     * When calling `refetch` due to context/token changes, any `context` prop specified
     * previously passed to the execute function will continue to override the hook
     * option's `context` prop.
     */
    async (queryOptions?: Omit<QueryLazyOptions<TVariables>, 'context'>) => {
      runLazyQuery(queryOptions)
    },
    [runLazyQuery],
  )

  return [runSearchLazyQuery, queryResultProps]
}
